import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Plus, Search, Pencil, Power, Trash2, KeyRound, Shield, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import CountryPhoneFields from "@/components/CountryPhoneFields";
import PasswordInput from "@/components/ui/PasswordInput";

const inputCls =
  "flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

export default function CollaboratorManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/collaborators", { params: { q: q || undefined } });
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("collab.load_failed", "Could not load collaborators"));
    } finally { setLoading(false); }
  }, [q, t]);

  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);

  const toggleActive = async (c) => {
    try {
      await api.patch(`/collaborators/${c.id}`, { active: !c.active });
      toast.success(c.active ? t("collab.suspended", "Collaborator suspended") : t("collab.activated", "Collaborator activated"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(t("collab.delete_confirm", "Permanently delete this collaborator?"))) return;
    try {
      await api.delete(`/collaborators/${c.id}`);
      toast.success(t("collab.deleted", "Collaborator deleted"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      // Detect if country changed — we route country updates through the
      // dedicated endpoint that enforces phone-country mismatch guard,
      // records USER_COUNTRY_UPDATE, and preserves every existing wallet.
      const originalRow = rows.find((r) => r.id === editing.id);
      const nextCountry = editing.country_code || null;
      const priorCountry = originalRow?.country_code || null;
      const countryChanged = nextCountry && nextCountry !== priorCountry;

      const payload = {
        name: editing.name, email: editing.email, phone: editing.phone,
        job_title: editing.job_title, notes: editing.notes, active: editing.active,
      };
      await api.patch(`/collaborators/${editing.id}`, payload);

      if (countryChanged) {
        try {
          await api.patch(`/admin/users/${editing.id}/country`, { country_code: nextCountry });
        } catch (err) {
          if (err.response?.status === 409) {
            const ok = window.confirm(
              (err.response.data?.detail || "") +
              "\n\nProceed anyway with this mismatched combination?"
            );
            if (!ok) throw err;
            await api.patch(`/admin/users/${editing.id}/country`, {
              country_code: nextCountry, mismatch_confirmed: true,
            });
          } else { throw err; }
        }
      }
      toast.success(t("collab.updated", "Collaborator updated"));
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const submitReset = async () => {
    if (!resetPwd || resetPwd.length < 6) {
      toast.error(t("collab.pwd_min", "Password must be at least 6 characters"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/collaborators/${resetting.id}/reset-password`, { password: resetPwd });
      toast.success(t("collab.pwd_reset", "Password reset"));
      setResetting(null); setResetPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="collab-mgmt-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Collaborators</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t("collab.management_title", "Collaborator Management")}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("collab.management_hint", "Trusted employees who help you operate the platform.")}</p>
        </div>
        <button data-testid="collab-create-btn" onClick={() => navigate("/collaborators/create")} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6]">
          <Plus className="w-4 h-4" /> {t("collab.new", "New collaborator")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="collab-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="h-9 flex-1 bg-transparent focus:outline-none text-sm" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50">
                <th className="px-4 py-2">Name</th><th className="px-4 py-2">Username</th><th className="px-4 py-2">Job title</th>
                <th className="px-4 py-2">Email</th><th className="px-4 py-2">Phone</th>
                <th className="px-4 py-2">Status</th><th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">{t("collab.empty", "No collaborators yet.")}</td></tr>
              ) : rows.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`collab-row-${c.username}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{c.name}</td>
                  <td className="px-4 py-3 text-slate-700">{c.username}</td>
                  <td className="px-4 py-3 text-slate-700">{c.job_title || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{c.email}</td>
                  <td className="px-4 py-3 text-slate-700">{c.phone}</td>
                  <td className="px-4 py-3">
                    <span className={"inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border " + (c.active ? "bg-emerald-50 text-emerald-700 border-emerald-500" : "bg-slate-100 text-slate-600 border-slate-300")}>
                      {c.active ? t("agents.active") : t("agents.inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 space-x-1 whitespace-nowrap">
                    <button data-testid={`collab-edit-${c.username}`} onClick={() => setEditing({ ...c })} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-300 text-slate-700 text-xs hover:bg-slate-50"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`collab-perms-${c.username}`} onClick={() => navigate(`/collaborators/${c.id}/permissions`)} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-[#0F62FE]/30 bg-[#EDF5FF] text-[#0F62FE] text-xs hover:bg-[#DCE9FF]"><Shield className="w-3.5 h-3.5" /> {t("collab.permissions", "Permissions")}</button>
                    <button data-testid={`collab-reset-${c.username}`} onClick={() => { setResetting(c); setResetPwd(""); }} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-300 text-slate-700 text-xs hover:bg-slate-50"><KeyRound className="w-3.5 h-3.5" /></button>
                    <button data-testid={`collab-toggle-${c.username}`} onClick={() => toggleActive(c)} className={"inline-flex items-center gap-1 h-8 px-2 rounded-md border text-xs " + (c.active ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50")}><Power className="w-3.5 h-3.5" /></button>
                    <button data-testid={`collab-delete-${c.username}`} onClick={() => del(c)} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-rose-300 text-rose-700 text-xs hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={t("collab.edit_title", "Edit collaborator")} onClose={() => setEditing(null)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("ui.full_name")}><input data-testid="collab-edit-name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} /></Field>
            <Field label={t("ui.email")}><input data-testid="collab-edit-email" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inputCls} /></Field>
            <Field label={t("collab.job_title", "Job title")}><input data-testid="collab-edit-job" value={editing.job_title || ""} onChange={(e) => setEditing({ ...editing, job_title: e.target.value })} className={inputCls} /></Field>
            <Field label={t("collab.notes", "Notes")} full><textarea data-testid="collab-edit-notes" rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className={inputCls} /></Field>
          </div>
          <div className="mt-3 space-y-2">
            <CountryPhoneFields
              phone={editing.phone}
              countryCode={editing.country_code}
              onPhoneChange={(v) => setEditing({ ...editing, phone: v })}
              onCountryChange={(v) => setEditing({ ...editing, country_code: v })}
              testIdPrefix="collab-edit"
            />
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> {t("ui.active_label")}</label>
          <div className="flex justify-end gap-2 pt-3">
            <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
            <button data-testid="collab-edit-save" onClick={saveEdit} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}</button>
          </div>
        </Modal>
      )}

      {resetting && (
        <Modal title={t("ui.reset_password_title", { username: resetting.username })} onClose={() => setResetting(null)}>
          <Field label={t("ui.new_password_min")}><PasswordInput data-testid="collab-reset-pwd" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className={inputCls} minLength={6} /></Field>
          <div className="flex justify-end gap-2 pt-3">
            <button onClick={() => setResetting(null)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
            <button data-testid="collab-reset-submit" onClick={submitReset} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.reset_password")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h2>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
